import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { EnquiryRequest } from '../../types/enquiry';
import { useToast } from '../../hooks/use-toast';

interface EnquiryItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: EnquiryRequest | null;
  onSave: (item: EnquiryRequest) => void;
  mode: 'view' | 'edit' | 'create';
  type: 'general' | 'drive' | 'syllabus' | 'contact' | 'offer';
}

const EnquiryItemDialog: React.FC<EnquiryItemDialogProps> = ({ 
  open, 
  onOpenChange, 
  item, 
  onSave, 
  mode,
  type 
}) => {
  const [formData, setFormData] = useState<EnquiryRequest | null>(null);
  const { toast } = useToast();
  const isReadOnly = mode === 'view';

  useEffect(() => {
    if (item) {
      setFormData({ ...item });
    } else if (mode === 'create') {
      const newItem: EnquiryRequest = {
        id: `enquiry-${Date.now()}`,
        emailId: '',
        mobileNo: '',
        fullName: '',
        isVerified: false
      };

      // Add type-specific fields
      if (type === 'drive') {
        newItem.jobRole = '';
      } else if (type === 'syllabus') {
        newItem.course = '';
      } else if (type === 'contact') {
        newItem.query = '';
        newItem.feedback = '';
      } else if (type === 'offer') {
        newItem.couponCode = '';
        newItem.course = '';
      }

      setFormData(newItem);
    }
  }, [item, mode, type]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (formData) {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSwitchChange = (checked: boolean) => {
    if (formData) {
      setFormData({ ...formData, isVerified: checked });
    }
  };

  const handleSave = () => {
    if (formData) {
      onSave(formData);
      toast({
        title: `${mode === 'create' ? 'Created' : 'Updated'} Successfully`,
        description: `The enquiry has been ${mode === 'create' ? 'created' : 'updated'}.`,
      });
      onOpenChange(false);
    }
  };

  if (!formData) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-[#0071bd]">
            {mode === 'view' ? 'View Enquiry Details' : mode === 'edit' ? 'Edit Enquiry Details' : 'Create New Enquiry'}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                readOnly={isReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emailId">Email ID</Label>
              <Input
                id="emailId"
                name="emailId"
                value={formData.emailId}
                onChange={handleChange}
                readOnly={isReadOnly}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="mobileNo">Mobile Number</Label>
              <Input
                id="mobileNo"
                name="mobileNo"
                value={formData.mobileNo}
                onChange={handleChange}
                readOnly={isReadOnly}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="isVerified">Verification Status</Label>
              <div className="flex items-center h-10 space-x-2">
                <Switch 
                  id="isVerified" 
                  checked={formData.isVerified} 
                  onCheckedChange={handleSwitchChange}
                  disabled={isReadOnly}
                />
                <span>{formData.isVerified ? 'Verified' : 'Not Verified'}</span>
              </div>
            </div>
          </div>

          {type === 'drive' && (
            <div className="space-y-2">
              <Label htmlFor="jobRole">Job Role</Label>
              <Input
                id="jobRole"
                name="jobRole"
                value={formData.jobRole || ''}
                onChange={handleChange}
                readOnly={isReadOnly}
              />
            </div>
          )}

          {(type === 'syllabus' || type === 'offer') && (
            <div className="space-y-2">
              <Label htmlFor="course">Course</Label>
              <Input
                id="course"
                name="course"
                value={formData.course || ''}
                onChange={handleChange}
                readOnly={isReadOnly}
              />
            </div>
          )}

          {type === 'offer' && (
            <div className="space-y-2">
              <Label htmlFor="couponCode">Coupon Code</Label>
              <Input
                id="couponCode"
                name="couponCode"
                value={formData.couponCode || ''}
                onChange={handleChange}
                readOnly={isReadOnly}
              />
            </div>
          )}

          {type === 'contact' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="query">Query</Label>
                <Textarea
                  id="query"
                  name="query"
                  value={formData.query || ''}
                  onChange={handleChange}
                  readOnly={isReadOnly}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="feedback">Feedback</Label>
                <Textarea
                  id="feedback"
                  name="feedback"
                  value={formData.feedback || ''}
                  onChange={handleChange}
                  readOnly={isReadOnly}
                  rows={3}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {isReadOnly ? 'Close' : 'Cancel'}
          </Button>
          {!isReadOnly && (
            <Button className="bg-[#0071bd] hover:bg-[#005a97]" onClick={handleSave}>
              Save Changes
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EnquiryItemDialog;
